import loadConfigFromMeta from '@embroider/config-meta-loader';
import { assert } from '@ember/debug';

/**
 * Shape of `config/environment.js` as seen by app code.
 *
 * Carried over verbatim from the former `app/config/environment.d.ts`, which the
 * Vite codemod replaced with `{ 5 known keys } & Record<string, unknown>`. That
 * generic shape degrades `config.API_ENDPOINT`, `config.cognito.userPoolId`,
 * `config.S3_BASE_HREF` and friends to `unknown` in every TS file that reads
 * config, so the real declarations are kept here instead.
 */
export interface ScaleflowConfig {
  environment: string;
  modulePrefix: string;
  podModulePrefix: string;
  locationType: 'history' | 'hash' | 'none' | 'auto';
  rootURL: string;
  APP: Record<string, unknown>;
  REDIRECT_URL: string;
  API_ENDPOINT: string;
  WSS_ENDPOINT: string;
  ALLOWED_API_DOMAINS: string; //comma seperated list of allowed domains
  S3_BASE_HREF: string;
  S3_BUCKET_NAME: string;
  posthog: {
    api_host: string;
    api_key: string;
    person_profiles: 'always' | 'never' | 'identified_only';
  };
  aws: {
    region: string;
  };
  storage: {
    AWSS3: {
      bucket: string;
      vdr_bucket: string;
      region: string;
    };
  };
  cognito: {
    autoRefreshSession: true;
    userPoolId: string;
    clientId: string;
    domain: string;
    identityPoolId: string;
    region: string;
  };
  vdr: {
    url: string;
    preview?: {
      url: string;
    };
  };
  CHALLENGE_URL: string; //URL to aws waf app integration script for solving silent browser challenge
  '@sentry/ember': {
    sentry: { enabled: boolean } & Record<string, unknown>;
  };
  gitlab: {
    credentialKeyId: string;
    /** Base64 DER (SPKI). `PLACEHOLDER_PUBLIC_KEY` until the KMS key is deployed. */
    credentialPublicKey: string;
  };
}

const config = loadConfigFromMeta('scaleflow') as unknown;

assert(
  'config is not an object',
  typeof config === 'object' && config !== null,
);
assert(
  'modulePrefix was not detected on your config',
  'modulePrefix' in config && typeof config.modulePrefix === 'string',
);
assert(
  'locationType was not detected on your config',
  'locationType' in config && typeof config.locationType === 'string',
);
assert(
  'rootURL was not detected on your config',
  'rootURL' in config && typeof config.rootURL === 'string',
);
assert(
  'APP was not detected on your config',
  'APP' in config && typeof config.APP === 'object',
);

export default config as ScaleflowConfig;
